import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader,
  DialogFooter, DialogTitle, DialogDescription, DialogClose,
} from '@/components/ui/dialog';
import { accountsApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';

interface Props {
  account: { id: string; email_address: string; smtp_host?: string; smtp_port?: number };
}

/**
 * Configura un SERVER DI INVIO PERSONALIZZATO (relay) per una casella.
 * Serve quando l'SMTP del provider rifiuta l'autenticazione (es. host.it: errore 535):
 * l'invio passa per un server alternativo che funziona, ma il mittente resta la tua email.
 */
export function SendingServerDialog({ account }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [host, setHost] = useState(account.smtp_host ?? '');
  const [port, setPort] = useState(account.smtp_port ?? 587);
  const [username, setUsername] = useState(account.email_address);
  const [password, setPassword] = useState('');
  const [acceptCert, setAcceptCert] = useState(false);

  const mut = useMutation({
    mutationFn: () =>
      accountsApi.updateSending(account.id, {
        smtpHost: host.trim(),
        smtpPort: port,
        smtpUsername: username.trim(),
        smtpPassword: password,
        smtpTlsRejectUnauthorized: acceptCert ? false : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      toast.success('Server di invio salvato e verificato. Ora puoi inviare.');
      setPassword('');
      setOpen(false);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Verifica fallita'),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" title="Server di invio (SMTP)">
          <Send className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Server di invio per {account.email_address}</DialogTitle>
          <DialogDescription>
            Usalo se il provider rifiuta l'invio (es. host.it, errore 535). Inserisci un server
            SMTP che funziona (Blagoweb, Brevo, ecc.): il mittente resterà la tua email. La
            ricezione (IMAP) non cambia.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="ss-host">Host SMTP</Label>
              <Input id="ss-host" placeholder="smtp-relay.brevo.com" value={host}
                onChange={(e) => setHost(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ss-port">Porta</Label>
              <Input id="ss-port" type="number" value={port}
                onChange={(e) => setPort(parseInt(e.target.value, 10) || 587)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ss-user">Utente (login del server di invio)</Label>
            <Input id="ss-user" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ss-pass">Password del server di invio</Label>
            <Input id="ss-pass" type="password" placeholder="••••••••" value={password}
              onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </div>
          <div className="flex items-center justify-between rounded-md border p-3">
            <div className="text-sm">
              <div className="font-medium">Accetta certificato non verificato</div>
              <div className="text-xs text-muted-foreground">Attivalo solo se il server usa un certificato condiviso.</div>
            </div>
            <Switch checked={acceptCert} onCheckedChange={setAcceptCert} />
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Annulla</Button>
          </DialogClose>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || !host.trim() || !username.trim() || !password}>
            {mut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salva e verifica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
